import { useState, useEffect } from 'react';
import { useStudioStore } from '@/store/useStudioStore';
import { Activity, Save, RotateCcw } from 'lucide-react';

/**
 * Routing Matrix Component
 * 
 * Provides a visual grid interface for managing audio signal routing between tracks and buses.
 * 
 * Features:
 * - Visual grid showing all tracks (rows) and buses (columns)
 * - Click cells to create/remove routing connections
 * - Real-time signal flow indicators
 * - Automatic persistence to project state
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5
 */

export const RoutingMatrix = () => {
  // Get state from store
  const channels = useStudioStore((state) => state.channels);
  const buses = useStudioStore((state) => state.buses);
  const connections = useStudioStore((state) => state.routeConnections);
  
  // Get actions from store
  const toggleRouteConnection = useStudioStore((state) => state.toggleRouteConnection);
  const saveRoutingConfiguration = useStudioStore((state) => state.saveRoutingConfiguration);
  const loadRoutingConfiguration = useStudioStore((state) => state.loadRoutingConfiguration);
  const resetRoutingToDefault = useStudioStore((state) => state.resetRoutingToDefault);
  
  const [signalActivity, setSignalActivity] = useState<Map<string, number>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  
  // Initialize default routing on mount
  useEffect(() => {
    if (connections.length === 0) {
      resetRoutingToDefault();
    }
    // Load saved configuration
    loadRoutingConfiguration();
  }, []);
  
  // Simulate signal activity (in real implementation, this would come from audio engine)
  useEffect(() => {
    const interval = setInterval(() => {
      const newActivity = new Map<string, number>();
      connections.forEach(conn => {
        if (conn.enabled) {
          const key = `${conn.trackId}-${conn.busId}`;
          newActivity.set(key, Math.random() * 0.8 + 0.2); // 0.2-1.0
        }
      });
      setSignalActivity(newActivity);
    }, 100);
    
    return () => clearInterval(interval);
  }, [connections]);
  
  // Handle connection toggle
  const handleToggleConnection = (trackId: string, busId: string) => {
    toggleRouteConnection(trackId, busId);
    setHasChanges(true);
  };
  
  // Check if connection exists and is enabled
  const isConnected = (trackId: string, busId: string): boolean => {
    const conn = connections.find(c => c.trackId === trackId && c.busId === busId);
    return conn?.enabled || false;
  };
  
  // Get signal activity level for a connection
  const getActivityLevel = (trackId: string, busId: string): number => {
    const key = `${trackId}-${busId}`;
    return signalActivity.get(key) || 0;
  };
  
  // Save configuration
  const handleSave = () => {
    saveRoutingConfiguration();
    setHasChanges(false);
  };
  
  // Reset to default
  const handleReset = () => {
    resetRoutingToDefault();
    setHasChanges(true);
  };

  return (
    <div className="flex flex-col h-full bg-[var(--daw-bg-secondary)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--daw-border)]">
        <div>
          <h3 className="text-lg font-semibold">Routing Matrix</h3>
          <p className="text-xs text-zinc-400 mt-1">
            Click cells to route tracks to buses
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-1.5 bg-[var(--daw-bg-elevated)] hover:bg-[var(--daw-bg-tertiary)] rounded text-sm flex items-center gap-2 transition-colors"
            title="Reset to default routing"
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${
              hasChanges
                ? 'bg-[var(--daw-accent-primary)] hover:bg-[var(--daw-accent-hover)] text-white'
                : 'bg-[var(--daw-bg-elevated)] text-zinc-500 cursor-not-allowed'
            }`}
            title="Save routing configuration"
          >
            <Save size={14} />
            Save
          </button>
        </div>
      </div>
      
      {/* Routing Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div className="inline-block min-w-full">
          {/* Column Headers (Buses) */}
          <div className="flex mb-2">
            <div className="w-32 flex-shrink-0" /> {/* Empty corner */}
            {buses.map(bus => (
              <div
                key={bus.id}
                className="w-20 flex-shrink-0 text-center px-2"
              >
                <div
                  className="text-xs font-medium py-2 rounded"
                  style={{ backgroundColor: `${bus.color}20`, color: bus.color }}
                >
                  {bus.name}
                </div>
              </div>
            ))}
          </div>
          
          {/* Rows (Tracks) */}
          {channels.map(channel => (
            <div key={channel.id} className="flex items-center mb-1">
              {/* Track Label */}
              <div className="w-32 flex-shrink-0 pr-2">
                <div className="text-xs font-medium py-2 px-3 bg-[var(--daw-bg-elevated)] rounded truncate">
                  {channel.name}
                </div>
              </div>
              
              {/* Connection Cells */}
              {buses.map(bus => {
                const connected = isConnected(channel.id, bus.id);
                const activity = getActivityLevel(channel.id, bus.id);
                
                return (
                  <div
                    key={`${channel.id}-${bus.id}`}
                    className="w-20 flex-shrink-0 px-2"
                  >
                    <button
                      onClick={() => handleToggleConnection(channel.id, bus.id)}
                      className={`w-full h-12 rounded border-2 transition-all relative overflow-hidden ${
                        connected
                          ? 'border-[var(--daw-accent-primary)] bg-[var(--daw-accent-primary)]/20 hover:bg-[var(--daw-accent-primary)]/30'
                          : 'border-[var(--daw-border)] bg-[var(--daw-bg-elevated)] hover:bg-[var(--daw-bg-tertiary)]'
                      }`}
                      title={`${connected ? 'Disconnect' : 'Connect'} ${channel.name} to ${bus.name}`}
                    >
                      {/* Signal Flow Indicator */}
                      {connected && activity > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Activity
                            size={16}
                            className="text-[var(--daw-accent-primary)] animate-pulse"
                            style={{
                              opacity: activity,
                              filter: `drop-shadow(0 0 ${activity * 4}px var(--daw-accent-primary))`
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Connection Indicator */}
                      {connected && (
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-[var(--daw-accent-primary)] transition-all"
                          style={{ height: `${activity * 100}%`, opacity: 0.3 }}
                        />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="mt-6 p-4 bg-[var(--daw-bg-elevated)] rounded border border-[var(--daw-border)]">
          <h4 className="text-sm font-medium mb-3">Legend</h4>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded border-2 border-[var(--daw-accent-primary)] bg-[var(--daw-accent-primary)]/20" />
              <span className="text-zinc-400">Connected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded border-2 border-[var(--daw-border)] bg-[var(--daw-bg-elevated)]" />
              <span className="text-zinc-400">Disconnected</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-[var(--daw-accent-primary)]" />
              <span className="text-zinc-400">Signal Activity</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded border-2 border-[var(--daw-accent-primary)] bg-[var(--daw-accent-primary)]/20 relative overflow-hidden">
                <div className="absolute bottom-0 left-0 right-0 bg-[var(--daw-accent-primary)] opacity-30" style={{ height: '60%' }} />
              </div>
              <span className="text-zinc-400">Signal Level</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
